import React, { useEffect, useRef } from "react";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import TopBarOverview from "@/ui/molecules/TopBarOverview";
import LayoutFillParentWithFooter from "@/ui/layouts/LayoutFillParentWithFooter";
import { Button } from "@proda-ai/ui-kit";

import type {
    TChartOfAccountsDefinition,
    TCompany,
    TNewCoADefinition,
} from "@/apis/odin/apiODIN";

import EditChartOfAccountsForm, {
    type CoAEditFormHandle,
} from "@/ui/organisms/coaEditForm/EditChartOfAccountsForm";

export type ViewCreateChartOfAccountsParams = {
    useState: () => {
        excludedCoANames: string[];
        coasRequestStatus: "idle" | "pending" | "rejected" | "resolved";
        availableCoAsForDatabaseId: TChartOfAccountsDefinition[];
        companies: TCompany[] | null;
        getCompaniesState: "idle" | "pending" | "rejected" | "resolved";
        isCreateDraftCoaPending: boolean;
        isCreateDraftCoaError: boolean;
    };
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        onSubmit: (data: TNewCoADefinition) => Promise<void>;
        getExcludedCoANames: () => Promise<void>;
        setCreateCoASelectedDatabases: (databaseIds: number[]) => void;
    };
};

export type ViewCreateChartOfAccountsProps = BoxProps & {
    navigateToDashboard: () => void;
};

export const createViewCreateChartOfAccounts = ({
    useState,
    useCases,
}: ViewCreateChartOfAccountsParams) => {
    const ViewCreateChartOfAccounts = ({
        navigateToDashboard,
        ...props
    }: ViewCreateChartOfAccountsProps) => {
        const {
            excludedCoANames,
            companies,
            getCompaniesState,
            coasRequestStatus,
            isCreateDraftCoaPending,
            isCreateDraftCoaError,
        } = useState();

        const { onSubmit, getExcludedCoANames, onMount, onUnmount } =
            useCases();

        // A ref to the child form so we can call onConfirm() and read isDirty
        const formRef = useRef<CoAEditFormHandle>(null);

        const isLoading =
            coasRequestStatus === "pending" ||
            isCreateDraftCoaPending ||
            getCompaniesState === "pending" ||
            coasRequestStatus === "idle" ||
            getCompaniesState === "idle";

        const isError =
            coasRequestStatus === "rejected" ||
            isCreateDraftCoaError ||
            getCompaniesState === "rejected";

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    ...(props.sx || {}),
                }}
                {...props}
            >
                <TopBarOverview
                    // If you'd like the top‐bar to show the typed name live,
                    // you could extend CoACreateFormHandle to expose a `currentName` prop.
                    title=""
                    backButtonLabel="Back to Charts of Accounts dashboard"
                    onBackButtonClick={navigateToDashboard}
                />

                <LayoutFillParentWithFooter
                    footerContent={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                padding: "1rem",
                            }}
                        >
                            <Button
                                onClick={navigateToDashboard}
                                size="large"
                                disabled={isLoading || isError}
                                pattern="danger"
                                style={{ marginRight: "1rem" }}
                            >
                                Discard Draft
                            </Button>

                            <Button
                                onClick={() => {
                                    // Trigger validation + submit inside the child form
                                    void formRef.current?.onConfirm();
                                }}
                                size="large"
                                disabled={isLoading || isError}
                            >
                                Create Chart of Accounts
                            </Button>
                        </Box>
                    }
                >
                    <EditChartOfAccountsForm
                        ref={formRef}
                        excludedCoANames={excludedCoANames}
                        companies={companies}
                        getCompaniesState={getCompaniesState}
                        getExcludedCoANames={getExcludedCoANames}
                        onCoASubmit={onSubmit}
                        onDiscardCoA={navigateToDashboard}
                        navigateToChartOfAccountsEditDetails={() => {}}
                        coaId={undefined}
                        coaDefinition={null}
                        coaContainsNonOperatingAccounts={false}
                        coaContainsSubcategories={false}
                        isLoading={isLoading}
                        isError={isError}
                    />
                </LayoutFillParentWithFooter>
            </Box>
        );
    };

    return ViewCreateChartOfAccounts;
};
